import { useForm } from 'react-hook-form'

import { useManagementGetContactActions, useManagementGetContacts } from '@apps-common/apis/management/contact'
import { useManagementRestoreUnitCancelled } from '@apps-common/apis/management/unit-cancelled'
import { ButtonRounded } from '@apps-common/components/elements/button'
import { InputSM, SelectInputSM, TextAreaSM } from '@apps-common/components/elements/form'
import {
  ModalBody,
  ModalCloseButton,
  ModalContainer,
  ModalFooter,
  ModalHeader,
  ModalHeaderContainer
} from '@apps-common/components/modals/modal'
import { ModalDismiss } from '@apps-common/config/modal'
import { CID_ACCOUNT_REGEX } from '@apps-common/config/regex'
import { useManagementUnitSelectItems } from '@apps-common/hooks/useManagementUnitSelectItems'
import { ICancelledRestoreModel } from '@apps-common/types/management/unit'
import { IUnitGenericDetailsModel } from '@apps-common/types/portal/unit'

export const RestoreUnitModal = ({
  onDismiss,
  componentProps
}: {
  onDismiss: (data?: string | null | undefined | number, role?: string) => void
  componentProps: { unit: IUnitGenericDetailsModel }
}) => {
  const { unit } = componentProps
  const {
    register,
    handleSubmit,
    formState: { isSubmitting, errors }
  } = useForm<ICancelledRestoreModel>({
    defaultValues: {
      unitId: unit.unitId,
      mCentreId: unit.mCentreId,
      bureauId: unit.bureauId,
      uPlanId: unit.planId,
      accountNo: unit.accountNo
    }
  })

  const { mutate, isPending } = useManagementRestoreUnitCancelled(true, (data) => {
    onDismiss(JSON.stringify(data), ModalDismiss.Confirm)
  })

  const { isLoading: selectItemsLoading, companies, plans } = useManagementUnitSelectItems()
  const {
    isLoading: contactsLoading,
    isError: contactsError,
    data: contacts
  } = useManagementGetContacts({
    search: ''
  })
  const { isLoading: contactActionsLoading, isError: contactActionsError, data: contactActions } = useManagementGetContactActions({})

  const onSubmit = (values: ICancelledRestoreModel) => mutate({ model: values })

  const loading = selectItemsLoading || contactsLoading || contactsError || contactActionsLoading || contactActionsError
  const updating = isSubmitting || isPending

  return (
    <ModalContainer innerClasses='!bg-surface-2'>
      <ModalHeaderContainer>
        <div className='flex items-start justify-between gap-[10px]'>
          <ModalHeader subtitle='Update' title='Restore device to active' subtitleClasses='capitalize' />
          <ModalCloseButton closeModal={() => onDismiss(null, ModalDismiss.Cancel)} />
        </div>
      </ModalHeaderContainer>
      <ModalBody classes='!pt-[80px] !pb-[100px] sm:!pt-[100px] sm:!pb-[80px] w-full'>
        <div className='bg-surface px-4 pt-4 border border-border rounded-2xl'>
          <form noValidate id='restoreCancelledUnitForm' onSubmit={handleSubmit(onSubmit)}>
            <div className='grid lg:grid-cols-2 gap-x-[20px]'>
              <SelectInputSM
                type='text'
                placeholder='Select Monitoring Centre'
                label='Monitoring Centre'
                id='monitoringCentreId'
                showLabel
                loading={loading}
                disabled={updating}
                defaultValue={companies.defaultValue}
                registration={register('mCentreId', {
                  required: {
                    value: true,
                    message: 'Required'
                  }
                })}
                options={companies.options}
                errors={errors.mCentreId}
              />
              <SelectInputSM
                type='text'
                placeholder='Select Bureau'
                label='Bureau'
                id='bureauId'
                showLabel
                loading={loading}
                disabled={updating}
                defaultValue={companies.defaultValue}
                registration={register('bureauId', {
                  required: {
                    value: true,
                    message: 'Required'
                  }
                })}
                options={companies.options}
                errors={errors.bureauId}
              />
              <SelectInputSM
                type='text'
                placeholder='Select Plan'
                label='Plan'
                id='uPlanId'
                showLabel
                loading={loading}
                disabled={updating}
                defaultValue={plans.defaultValue}
                registration={register('uPlanId', {
                  required: {
                    value: true,
                    message: 'Required'
                  }
                })}
                options={plans.options}
                errors={errors.uPlanId}
              />
              <InputSM
                type='text'
                placeholder='CID account number'
                label='CID account'
                id='accountNo'
                showLabel
                loading={loading}
                disabled={updating}
                registration={register('accountNo', {
                  required: {
                    value: true,
                    message: 'Required'
                  },
                  pattern: {
                    value: CID_ACCOUNT_REGEX,
                    message: '4 numerical characters only e.g. 0123'
                  }
                })}
                errors={errors.accountNo}
              />
              <SelectInputSM
                type='text'
                placeholder='Select Contact'
                label='Contact'
                id='contactId'
                showLabel
                loading={loading}
                disabled={updating}
                registration={register('contactId', {
                  required: {
                    value: true,
                    message: 'Required'
                  }
                })}
                options={(contacts || []).map((c) => ({ label: c.name, value: c.contactId }))}
                errors={errors.contactId}
              />
              <SelectInputSM
                type='text'
                placeholder='Select Action'
                label='Contact Action'
                id='contactNote'
                showLabel
                loading={loading}
                disabled={updating}
                registration={register('contactNote', {
                  required: {
                    value: true,
                    message: 'Required'
                  }
                })}
                options={(contactActions || []).map((c) => ({ label: c.action, value: c.action }))}
                errors={errors.contactNote}
              />
            </div>
            <TextAreaSM
              type='text'
              placeholder='Enter a note relating to this restoration...'
              label='Note'
              id='note'
              showLabel
              loading={loading}
              disabled={updating}
              registration={register('note', {
                required: false
              })}
              errors={errors.note}
            />
          </form>
        </div>
      </ModalBody>
      <ModalFooter>
        <div className='flex items-center justify-start gap-[10px]'>
          <ButtonRounded
            form='restoreCancelledUnitForm'
            type='submit'
            text='Submit'
            loading={loading || updating}
            className='action-success'
          />
          <ButtonRounded
            type='button'
            text='Cancel'
            loading={updating}
            className='action-neutral'
            callback={() => onDismiss(null, 'cancel')}
          />
        </div>
      </ModalFooter>
    </ModalContainer>
  )
}
